import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactQuill from "react-quill-new";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { title: "", excerpt: "", content_html: "", cover_image: "", published: true };

export default function AdminJournalEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!!id);
  const [pending, setPending] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (id) {
      api.get("/admin/journal").then((r) => {
        const found = r.data.find((p) => p.id === id);
        if (found) setForm(found);
        setLoading(false);
      });
    }
  }, [id]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB.");
    const r = new FileReader();
    r.onload = () => setForm((p) => ({ ...p, cover_image: r.result }));
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setPending(true);
    try {
      if (id) {
        await api.put(`/admin/journal/${id}`, form);
      } else {
        await api.post("/admin/journal", form);
      }
      toast.success("Saved");
      navigate("/admin/journal");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPending(false);
    }
  };

  if (loading) return <p className="eyebrow text-muted-foreground">Loading…</p>;

  const modules = {
    toolbar: [
      [{ header: [2, 3, false] }],
      ["bold", "italic", "underline", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  return (
    <div data-testid="admin-journal-edit">
      <Link to="/admin/journal" className="eyebrow text-muted-foreground mb-6 inline-block">← All journal posts</Link>
      <h1 className="font-serif text-5xl font-light mb-10">{id ? "Edit post" : "New journal post"}</h1>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-12">
        <div className="space-y-6">
          <div>
            <label className="eyebrow mb-2 block">Title *</label>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="journal-title"
              className="w-full bg-transparent border-b border-border focus:border-foreground py-3 outline-none font-serif text-2xl"
            />
          </div>
          <div>
            <label className="eyebrow mb-2 block">Excerpt</label>
            <textarea
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              rows={2}
              data-testid="journal-excerpt"
              className="w-full bg-transparent border border-border p-3 outline-none"
            />
          </div>
          <div>
            <label className="eyebrow mb-2 block">Content</label>
            <div className="border border-border" data-testid="journal-editor">
              <ReactQuill
                theme="snow"
                modules={modules}
                value={form.content_html}
                onChange={(v) => setForm((p) => ({ ...p, content_html: v }))}
              />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="eyebrow mb-2 block">Cover image</label>
            <div className="aspect-[4/3] bg-secondary mb-3">
              {form.cover_image
                ? <img src={form.cover_image} alt="preview" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><span className="eyebrow">No image</span></div>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
            <button type="button" onClick={() => fileRef.current?.click()} className="btn-outline-ink w-full" data-testid="journal-image-upload">
              {form.cover_image ? "Replace cover" : "Upload cover"}
            </button>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="journal-published" />
            <span className="eyebrow">Published</span>
          </label>
          <button type="submit" disabled={pending} className="btn-ink w-full" data-testid="journal-save">
            {pending ? "Saving…" : id ? "Save changes" : "Publish post"}
          </button>
        </div>
      </form>
    </div>
  );
}
